import { encodeSelectionContextV3, mentionType } from '@aline/common';
import { ElementApi } from 'platejs';
import type { Descendant, HtmlPlugin, TElement, Value } from 'platejs';
import type { PlateEditor, TPlateEditor } from 'platejs/react';
import { createPlatePlugin } from 'platejs/react';
import { Range } from 'slate';
import { AlineSelectionClipboardDataV3Schema } from 'src/editor/transforms/clipboard-schemas';
import { CLIPBOARD_ALINE_DATA_ATTR } from 'src/editor/transforms/general-transforms';
import { getClipboardData } from 'src/layout-engine';
import { pushNotification } from 'src/notifications';
import { sanitizeHtmlForEditorPaste } from 'src/utils/sanitize-html';
import { v4 as uuid4 } from 'uuid';

import { deserializeMarkdownWithMentions } from './ChatEditorMarkdown';
import type { ChatEditorMarkdownPlugin } from './ChatEditorMarkdown';
import { insertMentionAt, transformMentionLinks } from './ChatEditorMention';

export const MAX_SELECTION_MENTION_SIZE = 100_000; // about 10 pages no tables

/** Type helper to access markdown API from editor with MarkdownPlugin */
export type EditorWithMarkdownApi = TPlateEditor<Value, typeof ChatEditorMarkdownPlugin>;

/** Type helper to access html API from editor with HtmlPlugin */
export type EditorWithHtml = TPlateEditor<Value, typeof HtmlPlugin>;

export function getIsEditorWithMarkdownApi(editor: PlateEditor): editor is EditorWithMarkdownApi {
  return 'markdown' in editor.api;
}

function getIsEditorWithHtml(editor: PlateEditor): editor is EditorWithHtml {
  return 'html' in editor.api;
}

export function deserializeHtmlWithMentions(editor: EditorWithHtml, html: string): TElement[] {
  const sanitizedHtml = sanitizeHtmlForEditorPaste(html);
  const deserialized = editor.api.html.deserialize({ element: sanitizedHtml });
  const nodes = deserialized
    .map(transformMentionLinks)
    .filter((node): node is TElement => ElementApi.isElement(node));
  return nodes.length ? nodes : [{ children: [{ text: '' }], type: 'p' }];
}

/**
 * Serializes selected content to markdown and writes to clipboard.
 * Returns true if successful, false if no selection or serialization failed.
 */
function copySelectionToClipboard(
  editor: EditorWithMarkdownApi,
  clipboardData: DataTransfer
): boolean {
  // Only copy if there's an expanded selection (text is highlighted)
  if (!editor.selection || !Range.isExpanded(editor.selection)) return false;

  const fragment = editor.api.fragment();
  const markdown = editor.api.markdown.serialize({ value: fragment });
  if (!markdown) return false;
  clipboardData.setData('text/plain', markdown);
  return true;
}

/**
 * PlateJS plugin that handles:
 * 1. Pasting selection mentions from the Aline Editor
 * 2. Converting pasted HTML to structured Plate content
 * 3. Converting pasted Markdown to structured Plate content
 * 4. Copying/cutting editor content as Markdown on CMD+C/X
 *
 * @requires MarkdownPlugin - Must be included in the editor's plugins
 * @requires HtmlPlugin - Must be included in the editor's plugins for HTML paste support
 */
export const SelectionMentionPastePlugin = createPlatePlugin({
  handlers: {
    onCopy: ({ editor, event }) => {
      // Type guard narrows editor to EditorWithMarkdownApi
      if (!getIsEditorWithMarkdownApi(editor)) return;
      if (!event.nativeEvent.clipboardData) return;
      // Only copy if there's an expanded selection
      if (!editor.selection || !Range.isExpanded(editor.selection)) return;

      try {
        event.preventDefault();
        event.stopPropagation();
        copySelectionToClipboard(editor, event.nativeEvent.clipboardData);
        return true;
      } catch (error) {
        console.error('Failed to serialize editor content to Markdown:', error);
        return;
      }
    },
    onCut: ({ editor, event }) => {
      // Type guard narrows editor to EditorWithMarkdownApi
      if (!getIsEditorWithMarkdownApi(editor)) return;
      if (!event.nativeEvent.clipboardData) return;
      // Only cut if there's an expanded selection
      if (!editor.selection || !Range.isExpanded(editor.selection)) return;

      try {
        event.preventDefault();
        event.stopPropagation();

        // Copy to clipboard first
        const copied = copySelectionToClipboard(editor, event.nativeEvent.clipboardData);
        if (!copied) return;

        // Delete the selected content
        editor.tf.delete();
        return true;
      } catch (error) {
        console.error('Failed to cut editor content:', error);
        return;
      }
    },
    onPaste: ({ editor, event }) => {
      const html = event.nativeEvent.clipboardData?.getData('text/html');
      const hasAlineSelection = html?.includes(CLIPBOARD_ALINE_DATA_ATTR);

      if (!hasAlineSelection) {
        // Try HTML deserialization first if HTML content is available.
        if (html) {
          try {
            const isHtmlEditor = getIsEditorWithHtml(editor);
            if (isHtmlEditor) {
              const nodes = deserializeHtmlWithMentions(editor, html);
              /**
               * Only use HTML result if it produced meaningful text content.
               * Otherwise fall through to markdown deserialization, which handles
               * clipboard text/plain more reliably (e.g. content copied from Plate
               * editors may produce HTML that doesn't round-trip through the HTML
               * deserializer correctly).
               */
              if (hasNonEmptyText(nodes)) {
                event.preventDefault();
                event.stopPropagation();
                // Use insertFragment for proper inline paste behavior (not insertNodes which creates new blocks)
                editor.tf.insertFragment(nodes);
                // Move cursor to end of inserted content
                editor.tf.collapse({ edge: 'end' });
                return true;
              }
            }
          } catch (error) {
            // Fall through to try markdown deserialization...
            console.error('Failed to deserialize HTML paste:', error);
          }
        }

        // Fall back to Markdown deserialization from plain text.
        const plainText = event.nativeEvent.clipboardData?.getData('text/plain');
        if (plainText) {
          try {
            event.preventDefault();
            event.stopPropagation();

            const isMarkdownEditor = getIsEditorWithMarkdownApi(editor);
            if (!isMarkdownEditor) return;

            // Use insertFragment for proper inline paste behavior (not insertNodes which creates new blocks)
            editor.tf.insertFragment(deserializeMarkdownWithMentions(editor, plainText));
            // Move cursor to end of inserted content
            editor.tf.collapse({ edge: 'end' });
            return true;
          } catch {
            // Show toast for content that couldn't be parsed as markdown
            pushNotification({
              key: uuid4(),
              message: 'Could not paste content as formatted text. Pasting as plain text instead.',
              variant: 'warning',
            });
            // Insert as plain text since event.preventDefault() was already called
            editor.tf.insertText(plainText);
            return true;
          }
        }

        // Let Plate handle the default paste for non-Markdown content...
        return;
      }

      // Prevent default and handle Aline selection paste.
      event.preventDefault();
      event.stopPropagation();

      // Use async Clipboard API to read clipboard data.
      getClipboardData()
        .then(clipboardData => {
          if (!clipboardData) return;
          const { clipboardAlineSelection } = clipboardData;

          try {
            const parsed: unknown = JSON.parse(clipboardAlineSelection);
            const data = AlineSelectionClipboardDataV3Schema.parse(parsed);

            const encoded = encodeSelectionContextV3({
              documentId: data.documentId,
              focusSuggestionId: data.focusSuggestionId,
              html: data.html,
            });
            if (encoded.length > MAX_SELECTION_MENTION_SIZE) {
              pushNotification({
                key: uuid4(),
                message: 'Selection too large. Please select less text.',
                variant: 'warning',
              });
              return;
            }

            insertMentionAt(editor, {
              mentionNode: {
                children: [{ text: '' }],
                documentId: data.documentId,
                documentName: data.documentName,
                focusSuggestionId: data.focusSuggestionId,
                html: data.html,
                mentionTypeName: 'selection-v3',
                type: mentionType,
                value: data.documentName,
              },
            });
            // Move cursor to end (after the mention)
            editor.tf.collapse({ edge: 'end' });
          } catch (error) {
            console.error('Failed to parse selection clipboard data:', error);
          }
        })
        .catch(error => {
          console.error('Failed to read clipboard data:', error);
        });

      return true;
    },
  },
  key: 'selectionMentionPaste',
});

/**
 * Recursively checks if nodes contain any non-whitespace text.
 * Used to validate that HTML deserialization produced meaningful content
 * before committing to the HTML paste path (which would skip the markdown fallback).
 */
function hasNonEmptyText(nodes: Descendant[]): boolean {
  for (const node of nodes) {
    if ('text' in node && typeof node.text === 'string' && node.text.trim()) return true;
    if (ElementApi.isElement(node) && hasNonEmptyText(node.children)) return true;
  }
  return false;
}
